import { useState, useCallback, useRef, useEffect } from "react";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { lighten, darken } from "@mui/system";
import { Typography, Box, Paper, FormLabel } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme, alpha } from "@mui/material/styles";
import { Group } from "@mui/icons-material";
import { MyFormData } from "./form-context";
import { getMultiSelectStyles } from "@/lib/styles";
import FileUploadField from "@/components/FileUploadField";
import { useTranslation } from "react-i18next";

const animatedComponents = makeAnimated();

const OtherResources = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const multiSelectStyles = getMultiSelectStyles(theme);
  const isDarkMode = theme.palette.mode === "dark";

  const {
    ptwData,
    handleMultiSelect,
    handleFileChange,
    fileNames,
    CheckList,
    removeFile,
  } = MyFormData();

  const [portalEl, setPortalEl] = useState(null);
  const checklistSelectRef = useRef(null); // Ref for checklist_ids Select

  useEffect(() => {
    setPortalEl(document.body);
  }, []);

  // Memoized handler to prevent unnecessary re-renders
  const handleChecklistSelect = useCallback(
    (selectedOptions) => {
      handleMultiSelect("checklist_ids")(selectedOptions);
      // Restore scroll position or focus to prevent scrolling
      if (checklistSelectRef.current) {
        checklistSelectRef.current.focus();
      }
    },
    [handleMultiSelect]
  );

  return (
    <Box>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 3,
          borderRadius: 3,
          backgroundColor: isDarkMode
            ? darken(theme.palette.background.paper, 0.02)
            : lighten(theme.palette.background.paper, 0.5),
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Group color="secondary" />
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.secondary.main,
              fontWeight: 600,
            }}
          >
            {t("permitInfo.teamResourcesSection")}
          </Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <FormLabel
              sx={{
                color: theme.palette.text.primary,
                mb: 1,
                display: "block",
                fontWeight: 600,
              }}
            >
              {t("permitInfo.checklistsLabel")}
            </FormLabel>
            <Select
              ref={checklistSelectRef}
              placeholder={t("permitInfo.checklistsPlaceholder")}
              options={CheckList}
              components={animatedComponents}
              isMulti
              closeMenuOnSelect={false}
              blurInputOnSelect={false}
              value={ptwData?.checklist_ids}
              onChange={handleChecklistSelect}
              styles={{
                ...multiSelectStyles,
                menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                control: (provided, state) => ({
                  ...provided,
                  ...multiSelectStyles.control(provided, state),
                  borderColor: state.isFocused
                    ? theme.palette.secondary.main
                    : alpha(theme.palette.text.primary, 0.2),
                  borderWidth: state.isFocused ? 2 : 1,
                }),
              }}
              menuPortalTarget={portalEl}
              menuPosition="fixed"
              menuShouldScrollIntoView={false}
              aria-label={t("permitInfo.checklistsAria")}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormLabel
              sx={{
                color: theme.palette.text.primary,
                mb: 1,
                display: "block",
                fontWeight: 600,
              }}
            >
              {t("permitInfo.attachmentsLabel")}
            </FormLabel>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mb={2}
            >
              {t("permitInfo.attachmentsDescription") ||
                "Upload images or PDF files (max 10MB each)"}
            </Typography>

            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.5),
                border: `2px dashed ${alpha(theme.palette.text.primary, 0.2)}`,
                borderRadius: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: theme.palette.secondary.main,
                  bgcolor: alpha(theme.palette.secondary.main, 0.05),
                },
              }}
            >
              <FileUploadField
                multiple={true}
                accept="image/*,application/pdf"
                label={t("permitInfo.uploadFiles") || "Upload Files"}
                inputId="other-resources-file-upload"
                fileNames={fileNames || []}
                onChange={handleFileChange}
                onDelete={removeFile}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default OtherResources;
